/**
 * THE REQUIREMENTS group on the inquiry record — the record's reading of
 * `inquiry_platforms` (migration 48, owner ruling 5; IQ-9 / D19), and since unit 3.2 the
 * place a coordinator FIXES it.
 *
 * ===========================================================================
 * WHY THIS GROUP IS EDITABLE WHEN THE REST OF THE PANEL IS NOT
 * ===========================================================================
 * The owner's ruling, verbatim: "The record page is read-only and a mis-ticked
 * requirement stays forever — that is a defect a coordinator meets on day one.
 * Requirements must be editable on the record, and clearing must be reachable from the
 * browser." The server side existed first — `UpdateInquiryDto.platforms` with D18's
 * distinction (`undefined` = untouched, `[]` = clear), proven by the D19 cross-boundary
 * spec — and `inquiry-platforms.smoke.spec.ts` FILED the missing screen as a finding
 * rather than papering over it. This component is that screen.
 *
 * PERMISSION — MEASURED, NOT INVENTED. The edit affordance is gated on
 * `inquiries.edit`: that is what `PATCH /inquiries/:id` itself demands
 * (`inquiries.controller.ts:78`, `@RequirePerm('inquiries.edit')`), and it is the same
 * permission the record page's other edit already runs on (`canEdit` gates Add
 * Follow-up). No new permission is minted; a seat that can log a call can fix a tick.
 *
 * ===========================================================================
 * THE WIRE RULES, AS THE EDITOR HOLDS THEM
 * ===========================================================================
 *  · SAVE SENDS THE WHOLE SET, as `{platforms: [...]}` and NOTHING ELSE — the API's
 *    replace-not-delta shape. Clearing every tick sends `platforms: []`, which is the
 *    owner's "clearing must be reachable" made literal: `[]` = cleared is a different
 *    statement from an absent key, and the smoke asserts the `[]` ON THE WIRE.
 *  · AN UNTOUCHED SET CANNOT BE SENT. Save is disabled until the checked set or a
 *    detail differs from what the record holds (compared as `platform_id → detail`
 *    maps, the service's own comparison), so opening the editor and closing it — or
 *    ticking and unticking back to where it was — issues NO request at all. That keeps
 *    D18's `undefined` = untouched true from the browser, and it protects an audit
 *    fact: the attach rows' `created_by`/`created_at` survive because no identical
 *    replace is ever requested.
 *  · A RETIRED VALUE IS KEPT, NOT DROPPED. The picker offers what the server offers
 *    TODAY, but this inquiry may carry a value the owner has since retired or switched
 *    off for the country. The editor lists such a value too (by the name the record
 *    read gave it), because the save REPLACES the whole set: an editor that built its
 *    payload from the offered list alone would silently strip the retired value on an
 *    unrelated tick. The server tolerates already-attached ids for exactly this case
 *    (`inquiries.service.ts` `resolvePlatforms`' `already` set) — but says yes only to
 *    what is still attached, so the hint under the value warns that removing it is
 *    final.
 *    ⚠ THE TOLERANCE IS THE RETIREMENT REFUSALS ONLY — measured, not read off the
 *    comment: `resolvePlatforms` steps 3 (`active = 0`) and 4 (`country_platforms`
 *    switched off) carry the `already` exception; step 2 (an `applies_to` narrowed
 *    away from `inquiry`) does NOT, by the service's own design. An inquiry carrying
 *    such a value gets the server's 422 sentence in this editor's Alert on save, and
 *    the coordinator's way through is to untick that value — which is the sentence
 *    the server sends, shown rather than second-guessed.
 *
 * THE READ VIEW IS UNCHANGED, byte for byte: same `data-testid="inquiry-requirements"`
 * `<ul>`, same name/detail spans, same empty sentence — `inquiry-platforms.test.tsx`
 * and the D19 smoke read that structure, and an edit affordance must not move what they
 * measure. The editor renders under `inquiry-requirements-editor` instead, and only
 * while open.
 *
 * ⚠ THE GROUP STILL RENDERS EVEN WHEN EMPTY — the Tally-details reasoning on the record
 * page: OPTIONAL is not PERMISSIONED, and the empty sentence says which one this is.
 */

import { useMemo, useState } from 'react';
import { Alert, Button, Checkbox, TextAreaField } from '../../components/ui';
import { PropertyGroup } from './record-furniture';
import { createErrorText } from './errors';
import { useInquiryPlatformOptions, useUpdateInquiryPlatforms } from './use-inquiries';
import { useToast } from './toast';
import type { InquiryPlatformEntry } from './api';
import type { InquiryRecordDto } from '../../lib/inquiries-api';

/** `''` and whitespace are "no detail", spelled the way the API stores it: `null`. */
function normalizeDetail(text: string | null | undefined): string | null {
  const trimmed = (text ?? '').trim();
  return trimmed === '' ? null : trimmed;
}

/** One row of the editor: an offered value, or an attached one the vocabulary no longer offers. */
interface EditorEntry {
  id: string;
  name: string;
  /** True for a value this inquiry carries that the server no longer offers here. */
  unoffered: boolean;
}

export function RequirementsGroup({
  countryCode,
  inquiry,
  canEdit,
}: {
  countryCode: string;
  inquiry: InquiryRecordDto;
  canEdit: boolean;
}): React.JSX.Element {
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [on, setOn] = useState<Readonly<Record<string, boolean>>>({});
  const [detail, setDetail] = useState<Readonly<Record<string, string>>>({});

  /* The picker's source is fetched only while the editor is open: a read-only visit to
   * the record must not cost the options request. `inquiries.view` guards the route and
   * the page has already required it to render at all. */
  const options = useInquiryPlatformOptions(countryCode, editing);
  const update = useUpdateInquiryPlatforms(countryCode, inquiry.id);

  /** What the ROW holds, as the service compares it: `platform_id → detail`. */
  const baseline = useMemo(
    () =>
      new Map(
        inquiry.platforms.map((platform) => [
          platform.platform_id,
          normalizeDetail(platform.detail),
        ]),
      ),
    [inquiry.platforms],
  );

  const openEditor = (): void => {
    const ticked: Record<string, boolean> = {};
    const texts: Record<string, string> = {};
    for (const platform of inquiry.platforms) {
      ticked[platform.platform_id] = true;
      texts[platform.platform_id] = platform.detail ?? '';
    }
    setOn(ticked);
    setDetail(texts);
    update.reset();
    setEditing(true);
  };

  /* Offered values in the vocabulary's order, then any attached value the vocabulary no
   * longer offers (in the record's order — which is also the vocabulary's, from the read
   * join). Names come from the server on both branches; nothing here holds a label. */
  const entries: EditorEntry[] = useMemo(() => {
    const offered = options.data ?? [];
    const offeredIds = new Set(offered.map((option) => option.id));
    return [
      ...offered.map((option) => ({ id: option.id, name: option.name, unoffered: false })),
      ...inquiry.platforms
        .filter((platform) => !offeredIds.has(platform.platform_id))
        .map((platform) => ({
          id: platform.platform_id,
          name: platform.name,
          unoffered: true,
        })),
    ];
  }, [options.data, inquiry.platforms]);

  /** The CHECKED set in entry order, details trimmed to `null` — the payload shape. */
  const chosen: InquiryPlatformEntry[] = entries
    .filter((entry) => on[entry.id] === true)
    .map((entry) => ({ platform_id: entry.id, detail: normalizeDetail(detail[entry.id]) }));

  /* Dirty = the maps differ. Computed against the RECORD, not against "was anything
   * clicked": a tick that is unticked again leaves the editor clean, and Save stays
   * disabled — the untouched set structurally cannot reach the wire. */
  const dirty =
    chosen.length !== baseline.size ||
    chosen.some(
      (entry) =>
        !baseline.has(entry.platform_id) || baseline.get(entry.platform_id) !== entry.detail,
    );

  const save = (): void => {
    if (!dirty || update.isPending) return;
    update.mutate(chosen, {
      onSuccess: () => {
        toast.push({ text: 'Requirements updated' });
        setEditing(false);
      },
    });
  };

  return (
    <PropertyGroup label="Requirements">
      {editing ? (
        <div data-testid="inquiry-requirements-editor" className="flex flex-col gap-3">
          {update.isError ? (
            <Alert tone="danger">{createErrorText(update.error)}</Alert>
          ) : null}
          {options.isPending ? (
            <p className="text-label text-fg-secondary">Loading the list…</p>
          ) : options.isError ? (
            /* Without the offered list, unticking cannot be told apart from "never
             * offered" — so nothing is editable, and the sentence says why (the create
             * picker's rule: never an empty picker that looks like a fact). */
            <p className="text-label text-fg-secondary">
              The requirements list could not be read, so nothing can be changed right
              now. {createErrorText(options.error)}
            </p>
          ) : entries.length === 0 ? (
            <p className="text-label text-fg-secondary">
              Nothing is offered for inquiries in {countryCode} yet. The list is
              maintained by the group and switched on per country.
            </p>
          ) : (
            entries.map((entry) => {
              const checked = on[entry.id] === true;
              return (
                <div key={entry.id} className="flex flex-col gap-2">
                  <Checkbox
                    label={entry.name}
                    checked={checked}
                    onChange={(event) =>
                      setOn((current) => ({ ...current, [entry.id]: event.target.checked }))
                    }
                  />
                  {entry.unoffered ? (
                    <p className="text-label text-fg-muted">
                      No longer offered here. It stays while ticked; removed and saved,
                      it cannot be ticked back.
                    </p>
                  ) : null}
                  {checked ? (
                    <TextAreaField
                      label={`${entry.name} — details`}
                      rows={3}
                      maxLength={5000}
                      hint="What exactly is wanted. Kept if you untick and re-tick."
                      value={detail[entry.id] ?? ''}
                      onChange={(event) =>
                        setDetail((current) => ({
                          ...current,
                          [entry.id]: event.target.value,
                        }))
                      }
                    />
                  ) : null}
                </div>
              );
            })
          )}
          <div className="flex items-center gap-2">
            <Button
              onClick={save}
              disabled={!dirty || update.isPending}
              title={dirty ? undefined : 'Nothing has changed yet.'}
            >
              {update.isPending ? 'Saving…' : 'Save requirements'}
            </Button>
            <Button variant="ghost" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <>
          {inquiry.platforms.length === 0 ? (
            <p className="text-label text-fg-muted">
              No requirements recorded. Values come from the group&apos;s shared list.
            </p>
          ) : (
            <ul data-testid="inquiry-requirements" className="flex flex-col gap-2.5">
              {inquiry.platforms.map((platform) => (
                <li key={platform.platform_id} className="flex flex-col gap-1">
                  <span className="text-body13 font-medium text-fg">{platform.name}</span>
                  {platform.detail === null || platform.detail === '' ? (
                    <span className="text-label text-fg-muted">No details recorded</span>
                  ) : (
                    <span className="whitespace-pre-wrap text-label text-fg-secondary">
                      {platform.detail}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
          {canEdit ? (
            <Button variant="quiet" className="w-fit" onClick={openEditor}>
              Edit requirements
            </Button>
          ) : null}
        </>
      )}
    </PropertyGroup>
  );
}
